// Experiments Page: Lists experiments and allows creation of new ones

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import { Input } from "@/components/input";
import { Separator } from "@/components/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { TooltipProvider } from "@/components/tooltip";
import { deleteExperiment } from "@/http/api";
import { useExperiments, useExperimentsDailyDelivery, useUsers } from "@/http/api-hooks";
import { Experiment, ExperimentType } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { formatUTCDate, formatUTCDateTime } from "@/lib/utils";
import DailyDeliverySpark from "@/pages/experiments/components/DailyDeliverySpark";
import { DiagnosisStatusButton } from "@/pages/experiments/components/DiagnosisStatusButton";
import { getExperimentOutcomeOption } from "@/pages/experiments/components/experiment-outcome";
import {
  canRecordRolloutDurability,
  getExperimentRolloutOption,
} from "@/pages/experiments/components/experiment-rollout";
import {
  getExperimentStatus,
  getExperimentStatusLabel,
  getExperimentStatusVariant,
} from "@/pages/experiments/components/experiment-status";
import { ExperimentTagCell } from "@/pages/experiments/components/ExperimentTags";
import { RolloutDurabilityCell } from "@/pages/experiments/components/RolloutDurabilityCell";
import {
  buildDailyDeliveryMetricsForExperiment,
  buildDailyDeliveryParamsForExperiment,
  buildLast7DaysWindow,
} from "@/pages/experiments/daily-delivery-metrics";
import {
  ChevronLeft,
  ChevronRight,
  Edit as EditIcon,
  Eye,
  Loader2,
  MoreHorizontal,
  Search,
  Tag as TagIcon,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const experimentsPageSize = 20;
const experimentSearchDebounceMs = 300;
const experimentListParamConfig = {
  page: { name: "page", type: "number" as const, defaultValue: 1 },
  search: { name: "search", type: "string" as const, defaultValue: "" },
  tag: { name: "tag", type: "string" as const, defaultValue: "" },
};

// Calculate progress percentage based on dates
function getProgressPercentage(experiment: Experiment): number {
  const now = new Date();
  const startDate = experiment.start_date ? new Date(experiment.start_date) : null;
  const endDate = experiment.end_date ? new Date(experiment.end_date) : null;

  if (!startDate || !endDate) return 0;

  // If experiment hasn't started yet
  if (startDate > now) return 0;

  // If experiment has ended
  if (endDate < now) return 100;

  // Calculate progress for active experiments
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();

  if (totalDuration <= 0) return 0;

  return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
}

type DailyDeliveryCellProps = {
  experiment: Experiment;
  isActive: boolean;
  defaultStartDateTime: string;
  defaultEndDateTime: string;
  last7Dates: string[];
};

function DailyDeliveryCell({
  experiment,
  isActive,
  defaultStartDateTime,
  defaultEndDateTime,
  last7Dates,
}: DailyDeliveryCellProps) {
  const params = useMemo(
    () => buildDailyDeliveryParamsForExperiment(experiment, defaultStartDateTime, defaultEndDateTime),
    [defaultEndDateTime, defaultStartDateTime, experiment],
  );
  const shouldFetch = isActive && Boolean(params);
  const { data, isLoading, error } = useExperimentsDailyDelivery(params, shouldFetch);
  const days = useMemo(
    () => buildDailyDeliveryMetricsForExperiment(experiment, data?.rows || [], data?.targets || [], last7Dates),
    [data?.rows, data?.targets, experiment, last7Dates],
  );

  if (!isActive) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (isLoading) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }

  if (error) {
    return <span className="text-xs text-muted-foreground">N/A</span>;
  }

  if (days.length === 0) {
    return <span className="text-xs text-muted-foreground">No data</span>;
  }

  return <DailyDeliverySpark days={days} mode={experiment.type === ExperimentType.Global ? "global" : "custom"} />;
}

type StatusOutcomeCellProps = {
  experiment: Experiment;
  status: ReturnType<typeof getExperimentStatus>;
  outcomeOption: ReturnType<typeof getExperimentOutcomeOption>;
};

function StatusOutcomeCell({ experiment, status, outcomeOption }: StatusOutcomeCellProps) {
  if (outcomeOption) {
    return (
      <Badge variant={outcomeOption.badgeVariant} title={outcomeOption.description}>
        {outcomeOption.label}
      </Badge>
    );
  }

  if (status === "active") {
    return (
      <div className="flex items-center">
        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-green-500 transition-all duration-300"
            style={{ width: `${getProgressPercentage(experiment)}%` }}
          />
        </div>
      </div>
    );
  }

  return <Badge variant={getExperimentStatusVariant(status)}>{getExperimentStatusLabel(status)}</Badge>;
}

export default function ExperimentsPage() {
  const navigate = useNavigate();
  const { paramState, updateParam, updateParams } = useUrlParams(experimentListParamConfig);
  const page = Number.isInteger(paramState.page) && paramState.page > 0 ? paramState.page : 1;
  const search = paramState.search;
  const tagFilter = paramState.tag.trim();
  const [searchInput, setSearchInput] = useState(search);
  const {
    data: experimentsData,
    isLoading,
    isValidating,
    mutate,
  } = useExperiments({
    page,
    page_size: experimentsPageSize,
    search: search.trim() || undefined,
    tag: tagFilter || undefined,
  });
  const { data: users } = useUsers();

  const experiments = experimentsData?.data || [];
  const pagination = experimentsData?.meta;
  const { startDateTime, endDateTime, dates: last7Dates } = useMemo(() => buildLast7DaysWindow(), []);
  // One clock for the whole table, so every confirmation age is measured against the
  // same instant instead of drifting row by row as the list renders.
  const now = useMemo(() => new Date(), []);

  // State for delete confirmation dialog
  const [experimentToDelete, setExperimentToDelete] = useState<Experiment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchInput === search) {
      return;
    }

    const timeout = window.setTimeout(() => {
      updateParams({ page: 1, search: searchInput });
    }, experimentSearchDebounceMs);

    return () => window.clearTimeout(timeout);
  }, [search, searchInput, updateParams]);

  useEffect(() => {
    if (paramState.page !== page) {
      updateParam("page", page);
    }
  }, [page, paramState.page, updateParam]);

  useEffect(() => {
    if (!isValidating && pagination && page > Math.max(1, pagination.total_page)) {
      updateParam("page", Math.max(1, pagination.total_page));
    }
  }, [isValidating, page, pagination, updateParam]);

  const userMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (users) {
      users.forEach(user => {
        map[user.id] = user.name;
      });
    }
    return map;
  }, [users]);

  const handleDeleteClick = (experiment: Experiment) => {
    setExperimentToDelete(experiment);
  };

  const handleConfirmDelete = async () => {
    if (!experimentToDelete) return;

    const experimentName = experimentToDelete.name;
    setIsDeleting(true);
    try {
      await deleteExperiment(`/api/experiments/${experimentToDelete.id}`);
      // Refresh the experiments list
      mutate();
      setExperimentToDelete(null);
      toast.success(`Experiment "${experimentName}" has been archived`);
    } catch (error) {
      console.error("Failed to archive experiment:", error);
      toast.error("Failed to archive experiment. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setExperimentToDelete(null);
  };

  return (
    <>
      <div className="mb-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-sm">
          <label htmlFor="experiment-list-filter" className="sr-only">
            Filter experiments by ID or name
          </label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="experiment-list-filter"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Filter by experiment ID or name"
            className="pl-9 pr-9"
          />
          {isValidating && (
            <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-3">
          {tagFilter && (
            <div className="flex items-center gap-2 rounded-md border border-dashed px-2 py-1 text-sm">
              <TagIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              <span className="max-w-[12rem] truncate font-medium">{tagFilter}</span>
              <button
                type="button"
                className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => updateParams({ page: 1, tag: "" })}
                aria-label="Clear tag filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Button onClick={() => navigate("/experiments/create")}>Create Experiment</Button>
        </div>
      </div>

      <Separator className="my-6" />

      <TooltipProvider delayDuration={100}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-20 w-16 min-w-16 max-w-16 bg-background text-left">ID</TableHead>
              <TableHead className="sticky left-16 z-20 bg-background shadow-[1px_0_0_0_hsl(var(--border))] min-w-[200px]">
                Name
              </TableHead>
              <TableHead className="min-w-[128px]">Owner</TableHead>
              <TableHead className="min-w-[160px]">Tags</TableHead>
              <TableHead className="min-w-[192px]">Status</TableHead>
              <TableHead className="min-w-[176px]">Rollout</TableHead>
              <TableHead className="min-w-[112px]">Start Date</TableHead>
              <TableHead className="min-w-[112px]">End Date</TableHead>
              <TableHead className="text-center min-w-[64px]">Diagnosis</TableHead>
              <TableHead className="text-center min-w-[144px]">Daily Delivery</TableHead>
              <TableHead className="min-w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {experiments && experiments.length > 0 ? (
              experiments.map(exp => {
                const status = getExperimentStatus(exp);
                const outcomeOption = getExperimentOutcomeOption(exp.outcome);
                const rolloutOption = getExperimentRolloutOption(exp.rollout_status);
                const showsDurability = canRecordRolloutDurability(exp);
                const shouldShowDiagnosis = status !== "scheduled";
                const hasDiagnosticsHistory = Boolean(exp.diagnosis_last_completed_at);
                const shouldHideConcludedMissingDiagnosis = status === "concluded" && !hasDiagnosticsHistory;
                return (
                  <TableRow key={exp.id} className="group">
                    <TableCell className="sticky left-0 z-20 w-16 min-w-16 max-w-16 bg-background text-left transition-colors group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--background)))]">
                      <Button
                        variant="link"
                        className="h-auto p-0 font-mono text-xs tabular-nums"
                        onClick={() => navigate(`/experiments/${exp.id}`)}
                        aria-label={`View experiment ${exp.id}`}
                      >
                        {exp.id}
                      </Button>
                    </TableCell>
                    <TableCell className="sticky left-16 z-20 bg-background group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--background)))] transition-colors shadow-[1px_0_0_0_hsl(var(--border))]">
                      <Button
                        variant="link"
                        className="p-0 h-auto text-primary underline hover:opacity-80"
                        onClick={() => navigate(`/experiments/${exp.id}`)}
                        aria-label={`View experiment ${exp.name}`}
                      >
                        {exp.name}
                      </Button>
                    </TableCell>
                    <TableCell>{userMap[exp.owner] || exp.owner_user?.name || "-"}</TableCell>
                    <TableCell>
                      <ExperimentTagCell experiment={exp} />
                    </TableCell>
                    <TableCell>
                      <StatusOutcomeCell experiment={exp} status={status} outcomeOption={outcomeOption} />
                    </TableCell>
                    <TableCell>
                      {rolloutOption ? (
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant={rolloutOption.badgeVariant} title={rolloutOption.description}>
                            {rolloutOption.label}
                          </Badge>
                          {showsDurability && <RolloutDurabilityCell experiment={exp} now={now} />}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{formatUTCDate(exp.start_date)}</TableCell>
                    <TableCell>{formatUTCDate(exp.end_date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {!shouldShowDiagnosis || shouldHideConcludedMissingDiagnosis ? (
                          <span className="text-muted-foreground">-</span>
                        ) : (
                          <DiagnosisStatusButton
                            isPassing={exp.diagnosis_all_runs_succeeded}
                            summary={exp.diagnosis_summary}
                            latestRunLabel={
                              exp.diagnosis_last_completed_at
                                ? formatUTCDateTime(exp.diagnosis_last_completed_at)
                                : undefined
                            }
                            onClick={() => navigate(`/experiments/${exp.id}?tab=diagnostics`)}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-28 h-10 mx-auto flex items-center justify-center overflow-hidden">
                        <DailyDeliveryCell
                          experiment={exp}
                          isActive={status === "active"}
                          defaultStartDateTime={startDateTime}
                          defaultEndDateTime={endDateTime}
                          last7Dates={last7Dates}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon" aria-label="More Options">
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuGroup>
                              <DropdownMenuItem onSelect={() => navigate(`/experiments/${exp.id}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Open
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => navigate(`/experiments/${exp.id}?edit=true`)}>
                                <EditIcon className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                              <DropdownMenuItem onSelect={() => handleDeleteClick(exp)} className="text-destructive">
                                <Trash2 className="mr-2 h-4 w-4" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground">
                  {isLoading
                    ? "Loading..."
                    : search.trim() || tagFilter
                      ? "No experiments match this filter."
                      : "No experiments found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {pagination && pagination.total > 0
              ? `${(pagination.page - 1) * pagination.page_size + 1}-${Math.min(
                  pagination.page * pagination.page_size,
                  pagination.total,
                )} of ${pagination.total} experiments`
              : "0 experiments"}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous experiments page"
              title="Previous page"
              disabled={isLoading || isValidating || page <= 1}
              onClick={() => updateParam("page", Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-24 text-center text-sm text-muted-foreground">
              Page {pagination?.page ?? page} of {Math.max(1, pagination?.total_page ?? 0)}
            </span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next experiments page"
              title="Next page"
              disabled={isLoading || isValidating || !pagination || page >= pagination.total_page}
              onClick={() => updateParam("page", page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </TooltipProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog open={experimentToDelete !== null} onOpenChange={open => !open && handleCancelDelete()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Experiment</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive {experimentToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
